/**
 * Copyright 2020 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: LicenseRef-.amazon.com.-AmznSL-1.0
 * Licensed under the Amazon Software License  http://aws.amazon.com/asl/
 */

import _ from 'lodash';
import { observer } from 'mobx-react';
import { Button } from 'semantic-ui-react';
import c from 'classnames';

import Header from './Header';
import Description from './Description';
import ErrorPointer from './ErrorPointer';

// expected props
// - form (via props)
// - field (via props), this is the mobx form field object
// - className (via props)
//
// The following props are to support existing React Semantic UI props:
// - disabled (via props), default to false
// - size (via props), default to small
const Component = observer(({ field, disabled = false, size = 'small', className = 'mb4' }) => {
  const { id, value, sync, error = '', extra = {} } = field;
  const { yesLabel = 'Yes', noLabel = 'No' } = extra;
  const hasError = !_.isEmpty(error); // IMPORTANT do NOT use field.hasError
  const isDisabled = field.disabled || disabled;
  const disabledClass = isDisabled ? 'disabled' : '';
  const errorClass = hasError ? 'error' : '';
  const yesSelected = (_.isBoolean(value) && value === true) || value === 'true';
  const noSelected = (_.isBoolean(value) && value === false) || value === 'false';
  const handleClick = toAssign => event => {
    event.preventDefault();
    event.stopPropagation();
    sync(toAssign);
    field.validate({ showErrors: true });
  };

  const yesAttributes = {
    onClick: handleClick(true),
    disabled: isDisabled,
  };
  const noAttributes = {
    onClick: handleClick(false),
    disabled: isDisabled,
  };

  if (yesSelected) yesAttributes.color = 'teal';
  if (noSelected) noAttributes.color = 'teal';

  return (
    <div className={c(className, errorClass, disabledClass)}>
      <div className="flex flex-wrap mb1">
        <Header field={field} className="mt1 mb0 mr2" />
        <div>
          <Button.Group id={id} size={size}>
            <Button {...yesAttributes}>{yesLabel}</Button>
            <Button.Or />
            <Button {...noAttributes}>{noLabel}</Button>
          </Button.Group>
        </div>
      </div>
      <Description field={field} />
      <ErrorPointer field={field} />
    </div>
  );
});

export default Component;
